import Loader from '@/components/Loader';
import { calculateCogsInEUR, groupCogsByCurrency } from '@/lib/cogs';
import { formatPrice } from '@/lib/helpers';
import { TAgentService } from '@/models/agents';
import { useRates } from '@/models/rates';
import {
  TService,
  useExcludeAgentService,
  useIncludeAgentService,
  useIncludedAgentServices,
} from '@/models/services';
import {
  ActionIcon,
  Box,
  Center,
  Divider,
  Flex,
  List,
  Popover,
  Table,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { PiCheck, PiMinusCircleFill, PiPackage } from 'react-icons/pi';
import IncludeAgentServiceButton from './IncludeAgentServiceButton';

function Summary({ cogs }: { cogs: TAgentService[] }) {
  const { data: rates, isLoading } = useRates();
  const agentServicesCogs = useMemo(() => groupCogsByCurrency(cogs), [cogs]);
  const converted = !cogs
    .map((c) => c.currency)
    .every((c) => c.toUpperCase() === 'EUR');

  if (isLoading || !rates) {
    return <>...</>;
  }

  return (
    <>
      <Table
        fz={'xs'}
        fw={500}
        highlightOnHover
        highlightOnHoverColor={'gray.3'}
      >
        <Table.Tbody>
          <Table.Tr>
            <Table.Td>
              {converted ? (
                <>
                  Estimated COGS value in EUR on{' '}
                  {dayjs(rates!.timestamp * 1000).format('DD.MM.YYYY')}
                </>
              ) : (
                <></>
              )}
            </Table.Td>
            <Table.Td ta={'right'}>
              {formatPrice(
                calculateCogsInEUR(agentServicesCogs, rates!),
                'EUR',
              )}
            </Table.Td>
          </Table.Tr>
        </Table.Tbody>
      </Table>
    </>
  );
}

function AgentServiceRow({ service }: { service: TAgentService }) {
  const [opened, { close, open }] = useDisclosure(false);
  return (
    <Flex align="center">
      {service.deliverables.length > 1 && (
        <Popover opened={opened} onClose={close}>
          <Popover.Target>
            <ActionIcon
              variant="transparent"
              onMouseEnter={open}
              onMouseLeave={close}
            >
              <PiPackage size={16} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown>
            <List icon={<PiCheck size={16} />}>
              {service.deliverables.map((d) => (
                <List.Item key={d.title}>
                  <Text size={'xs'} variant="dimmed" truncate="end">
                    {d.title}
                  </Text>
                  {d.description && (
                    <Text size={'xs'} c="dimmed" mt={2}>
                      {d.description}
                    </Text>
                  )}
                </List.Item>
              ))}
            </List>
          </Popover.Dropdown>
        </Popover>
      )}
      <Text fw={300} size="xs" truncate="end">
        {service.name}{' '}
        <span style={{ opacity: 0.5 }}>({service.agentName})</span>
      </Text>
    </Flex>
  );
}
export default function AgentServices({ service }: { service: TService }) {
  const { data: includedAgentServices, isLoading } = useIncludedAgentServices(
    service.id,
  );
  const { mutate: includeAgentService } = useIncludeAgentService(service.id);
  const { mutate: excludeAgentService } = useExcludeAgentService(service.id);

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : !includedAgentServices?.length ? (
        <Center m="sm">
          <Text size="xs">No agent services used.</Text>
        </Center>
      ) : (
        <>
          <Table
            fz={'xs'}
            highlightOnHover
            highlightOnHoverColor="gray.2"
            layout="fixed"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th fw={300}>Service</Table.Th>
                <Table.Th ta={'right'} fw={300} w={80}>
                  COGS
                </Table.Th>
                <Table.Th w={30}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {includedAgentServices.map((e) => (
                <Table.Tr key={e.id}>
                  <Table.Td p={0} pl="xs">
                    <AgentServiceRow service={e} />
                  </Table.Td>
                  <Table.Td p={0} ta={'right'}>
                    {formatPrice(e.cogs, e.currency)}
                  </Table.Td>
                  <Table.Td p={0}>
                    <ActionIcon
                      variant="transparent"
                      onClick={() => {
                        excludeAgentService(e, {
                          onSuccess: () => {
                            notifications.show({
                              title: `Excluded successfully`,
                              message: `Agent service "${e.name}" excluded successfully`,
                              color: 'green',
                              position: 'top-right',
                            });
                          },
                        });
                      }}
                    >
                      <PiMinusCircleFill size={18} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Divider />
          <Summary cogs={includedAgentServices} />
        </>
      )}
      <Box ta={'right'} mt={'sm'}>
        <IncludeAgentServiceButton
          included={includedAgentServices ?? []}
          onSelected={(agentService) => {
            includeAgentService(agentService, {
              onSuccess: () => {
                notifications.show({
                  title: 'Included successfully',
                  message: `Agent service "${agentService.name}" included successfully`,
                  color: 'green',
                  position: 'top-right',
                });
              },
            });
          }}
        />
      </Box>
    </>
  );
}
